import {
  Box,
  H6,
  MarketplaceAdCard,
  MarketplaceProductCard,
  P4,
  Stack,
} from "@noom/wax-component-library";

import { AdCardVariant } from "gql/graphql";

import { useVisibleOnceRef } from "../../../hooks/useVisibleOnceRef";
import type { MarketplaceAd, MarketplacePlacement } from "../hooks/useMarketplaceAds";

import { HtmlContent } from "./HtmlContent";

// Responsive card width: full viewport minus the space taken by the
// container's left padding (16px), the inter-card gap (16px), and the
// next-card peek (31px), clamped between 240px and the Figma tile size
// of 312px.
const CARD_WIDTH = "clamp(240px, calc(100vw - 63px), 312px)";
// Maintain the original 312:292 aspect ratio across all viewport sizes for ads.
const AD_CARD_ASPECT_RATIO = "312 / 292";
// Maintain the original 312:312 aspect ratio across all viewport sizes for products.
const PRODUCT_CARD_ASPECT_RATIO = "312 / 312";

// WAX card props are typed as `string` but at runtime the values are
// passed straight into `React.createElement(Heading, ..., title)` as
// children, so any ReactNode renders correctly. This helper wraps CMS
// text in a sanitized `<HtmlContent>` and casts to `string` to satisfy
// the WAX type declarations without losing inline markup like <sup>.
const htmlCardProp = (html: string): string =>
  (<HtmlContent html={html} />) as unknown as string;

type MarketplaceCarouselProps = {
  placement: MarketplacePlacement;
  onAdTapped: (ad: MarketplaceAd, placementName: string) => void;
  onAdViewed: (ad: MarketplaceAd, placementName: string) => void;
};

const MarketplaceCard = ({
  ad,
  cardVariant,
  onTap,
  onViewed,
}: {
  ad: MarketplaceAd;
  cardVariant: AdCardVariant;
  onTap: () => void;
  onViewed: () => void;
}) => {
  const { content } = ad;
  const impressionRef = useVisibleOnceRef(onViewed);

  if (cardVariant === AdCardVariant.Ad) {
    return (
      <MarketplaceAdCard
        ref={impressionRef}
        title={htmlCardProp(content.title)}
        body={htmlCardProp(content.description)}
        backgroundImageUrl={content.backgroundImageUrl ?? ""}
        label={content.pill?.text ? htmlCardProp(content.pill.text) : undefined}
        onClick={onTap}
        width={CARD_WIDTH}
        minWidth={CARD_WIDTH}
        sx={{ aspectRatio: AD_CARD_ASPECT_RATIO }}
      />
    );
  }

  const isProductVariant = !!content.productImageUrl;

  return (
    <MarketplaceProductCard
      ref={impressionRef}
      variant={isProductVariant ? "product" : "exploration"}
      title={htmlCardProp(content.title)}
      body={htmlCardProp(content.description)}
      productImageUrl={isProductVariant ? content.productImageUrl || undefined : undefined}
      backgroundImageUrl={isProductVariant ? undefined : content.backgroundImageUrl || undefined}
      label={content.pill?.text ? htmlCardProp(content.pill.text) : undefined}
      ctaLabel={content.ctaText ? htmlCardProp(content.ctaText) : ""}
      onClick={onTap}
      width={CARD_WIDTH}
      minWidth={CARD_WIDTH}
      sx={{ aspectRatio: PRODUCT_CARD_ASPECT_RATIO }}
    />
  );
};

/**
 * One horizontally scrollable row of marketplace cards. Uses native CSS
 * scroll snapping (no carousel library): fixed-width cards with the next
 * card peeking past the viewport edge.
 */
export const MarketplaceCarousel = ({
  placement,
  onAdTapped,
  onAdViewed,
}: MarketplaceCarouselProps) => {
  const { placementContent, ads } = placement;

  return (
    <Stack
      spacing={4}
      width="100%"
      data-cy={`marketplace-carousel-${placement.placementName}`}
    >
      <Stack spacing={1}>
        <H6 fontWeight="400">
          <HtmlContent html={placementContent.title} />
        </H6>
        {placementContent.description && (
          <P4>
            <HtmlContent html={placementContent.description} />
          </P4>
        )}
      </Stack>
      <Box
        display="flex"
        gap={4}
        overflowX="auto"
        scrollSnapType="x mandatory"
        ml={-4}
        mr={-4}
        pl={4}
        sx={{
          scrollbarWidth: "none",
          scrollPaddingInlineStart: "16px",
          "::-webkit-scrollbar": { display: "none" },
          "> *": { scrollSnapAlign: "start", flex: `0 0 ${CARD_WIDTH}` },
          "::after": { content: '""', minWidth: "16px", flexShrink: 0 },
        }}
      >
        {ads.map((ad, position) => (
          <MarketplaceCard
            key={`${ad.adName}-${position}`}
            ad={ad}
            cardVariant={placementContent.cardVariant}
            onTap={() => onAdTapped(ad, placement.placementName)}
            onViewed={() => onAdViewed(ad, placement.placementName)}
          />
        ))}
      </Box>
    </Stack>
  );
};
